import { db } from '../firebase';
import { collection, getDoc, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, where } from 'firebase/firestore';
import { fetchEvents, updateEvent, deleteEvent } from './eventService.js';
import { getEffectiveUserId, getUserById } from './userService';

const ressourceCollection = collection(db, "ressources");

// Récupérer les ressources de l'utilisateur dans l'ordre en utilisant le champ `order`
export const fetchRessources = async () => {
  const userId = await getEffectiveUserId();
  const user = await getUserById(userId);
  const maxRessources = user.plan?.maxRessources ?? Infinity;

  const ressourceQuery = query(
    ressourceCollection,
    where("userId", "==", userId),
    orderBy("order")
  );
  const querySnapshot = await getDocs(ressourceQuery);
  // On limite le nombre de ressources retournées
  return querySnapshot.docs.slice(0, maxRessources).map(doc => ({ id: doc.id, ...doc.data() })) || [];
};

// Ajouter une ressource avec un champ `order` et le userId
export const addRessource = async (ressource) => {
  const userId = await getEffectiveUserId();
  const user = await getUserById(userId);
  const maxRessources = user.plan?.maxRessources ?? Infinity;
  const ressources = await fetchRessources();
  if (ressources.length >= maxRessources) {
    // On refuse l'ajout si la limite est atteinte
    return null;
  }
  const newOrder = ressources.length > 0 ? ressources[ressources.length - 1].order + 1 : 0;
  const newRessource = { ...ressource, order: newOrder, userId };
  const docRef = await addDoc(ressourceCollection, newRessource);
  return { id: docRef.id, ...newRessource };
};

// Mettre à jour une ressource (par exemple, changer le nom ou l'ordre)
export const updateRessource = async (ressource) => {
  if (!ressource) return;
  const userId = await getEffectiveUserId();
  // On s'assure que la ressource reste bien liée au mainUserId
  const ressourceRef = doc(db, "ressources", ressource.id);
  await updateDoc(ressourceRef, { ...ressource, userId });
};

// Supprimer une ressource, mettre à jour l'ordre des autres et supprimer les événements associés
export const deleteRessource = async (ressourceId) => {
  const userId = await getEffectiveUserId();
  const ressourceRef = doc(db, "ressources", ressourceId);

  // Supprimer la ressource
  await deleteDoc(ressourceRef);

  // Mettre à jour l'ordre des autres ressources
  const ressources = await fetchRessources();
  let newOrder = 0;
  for (const ressource of ressources) {
    if (ressource.order !== newOrder) {
      const ressourceDocRef = doc(db, "ressources", ressource.id);
      await updateDoc(ressourceDocRef, { order: newOrder, userId });
    }
    newOrder++;
  }

  // Récupérer tous les événements de l'utilisateur
  const events = await fetchEvents();
  for (const event of events) {
    // Filtrer les ressources pour enlever celle supprimée
    if (event.ressources && Array.isArray(event.ressources)) {
      const updatedRessources = event.ressources.filter(r => r.ressourceId !== ressourceId);
      if (updatedRessources.length === 0) {
        // S'il n'y a plus aucune ressource, supprimez l'événement
        await deleteEvent(event.id);
      } else {
        // Sinon, mettre à jour l'événement avec le nouvel array de ressources
        await updateEvent({ ...event, ressources: updatedRessources });
      }
    }
  }
};

export const getRessource = async (ressourceId) => {
  if (!ressourceId) return null;
  const userId = await getEffectiveUserId();
  const ressourceRef = doc(db, "ressources", ressourceId);
  const ressourceSnapshot = await getDoc(ressourceRef);
  if (!ressourceSnapshot.exists()) {
    return null;
  }
  const data = ressourceSnapshot.data();
  // On vérifie que la ressource appartient bien au mainUserId
  if (data.userId !== userId) return null;
  return { id: ressourceSnapshot.id, ...data };
};
